<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import {
    getUnreadCount,
    listNotifications,
    markAllRead,
    markRead,
} from '../services/api/notifications.api';
import {
    off as offEvent,
    offConnect,
    on as onEvent,
    onConnect,
} from '../services/events.service';

const PANEL_WIDTH = 360;
const PANEL_GAP = 8;

const open = ref(false);
const root = ref(null);
const buttonEl = ref(null);
const panelEl = ref(null);
// Panel is teleported to <body> so the sidebar's overflow doesn't clip it;
// position is computed from the button's bounding rect each time it opens.
const panelPos = ref({ top: 0, left: 0 });
const unread = ref(0);
const items = ref([]);
const loading = ref(false);
const loadError = ref('');

const formatter = computed(() =>
    new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
    })
);

const severityClass = (s) => {
    if (s === 'ERROR') return 'notification-bell__row--error';
    if (s === 'WARN') return 'notification-bell__row--warn';
    return 'notification-bell__row--info';
};

async function loadUnread() {
    try {
        const res = await getUnreadCount();
        unread.value = Number(res?.count ?? 0);
    } catch (err) {
        // Stale count is preferable to a noisy error here — the next poll retries.
        console.warn('Failed to refresh unread count', err);
    }
}

async function loadList() {
    loading.value = true;
    loadError.value = '';
    try {
        const page = await listNotifications({ page: 0, size: 20 });
        items.value = page?.content || [];
    } catch (err) {
        loadError.value = err?.message || 'Failed to load notifications';
    } finally {
        loading.value = false;
    }
}

function positionPanel() {
    const btn = buttonEl.value;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    // Anchor the panel below the button, prefer right-aligning to the
    // button so the look matches the sidebar; clamp to the viewport so
    // narrow windows don't push it off-screen on the left.
    const margin = 8;
    let left = rect.right - PANEL_WIDTH;
    if (left < margin) left = margin;
    if (left + PANEL_WIDTH > window.innerWidth - margin) {
        left = Math.max(margin, window.innerWidth - PANEL_WIDTH - margin);
    }
    panelPos.value = {
        top: rect.bottom + PANEL_GAP,
        left,
    };
}

async function toggle() {
    if (open.value) {
        open.value = false;
        return;
    }
    positionPanel();
    open.value = true;
    await loadList();
}

function onOutsideClick(event) {
    if (!open.value) return;
    if (buttonEl.value && buttonEl.value.contains(event.target)) return;
    if (panelEl.value && panelEl.value.contains(event.target)) return;
    open.value = false;
}

function onReflow() {
    if (open.value) positionPanel();
}

watch(open, (isOpen) => {
    if (isOpen) {
        setTimeout(() => document.addEventListener('mousedown', onOutsideClick), 0);
        window.addEventListener('resize', onReflow);
        window.addEventListener('scroll', onReflow, true);
    } else {
        document.removeEventListener('mousedown', onOutsideClick);
        window.removeEventListener('resize', onReflow);
        window.removeEventListener('scroll', onReflow, true);
    }
});

async function onRowClick(item) {
    if (!item.read) {
        try {
            await markRead(item.id);
            item.read = true;
            unread.value = Math.max(0, unread.value - 1);
        } catch (err) {
            console.warn('Failed to mark notification read', err);
        }
    }
}

async function onMarkAll() {
    try {
        await markAllRead();
        items.value = items.value.map((n) => ({ ...n, read: true }));
        unread.value = 0;
    } catch (err) {
        console.warn('Failed to mark all read', err);
    }
}

function onNotificationCreated(data) {
    // Prepend the live notification and bump unread. If the dropdown is
    // closed we don't refetch — the row will appear next time the user
    // opens it (the cached items array stays in sync as long as the WS
    // remains connected).
    items.value = [{ ...data }, ...items.value];
    unread.value = unread.value + 1;
}

function onWsConnect() {
    // Refresh the unread count whenever the socket comes up (initial
    // connect + reconnect) so any events missed while offline are
    // accounted for in the badge.
    loadUnread();
}

loadUnread();
onEvent('notification.created', onNotificationCreated);
onConnect(onWsConnect);

onBeforeUnmount(() => {
    offEvent('notification.created', onNotificationCreated);
    offConnect(onWsConnect);
    document.removeEventListener('mousedown', onOutsideClick);
    window.removeEventListener('resize', onReflow);
    window.removeEventListener('scroll', onReflow, true);
});
</script>

<template>
    <div ref="root" class="notification-bell">
        <button
            ref="buttonEl"
            type="button"
            class="notification-bell__button"
            :aria-expanded="open"
            :aria-label="`Notifications${unread ? ', ' + unread + ' unread' : ''}`"
            @click="toggle"
        >
            <j-icon icon-id="icon-bell" class="notification-bell__icon" />
            <span v-if="unread > 0" class="notification-bell__badge" aria-hidden="true">
                {{ unread > 99 ? '99+' : unread }}
            </span>
        </button>
        <Teleport to="body">
            <div
                v-if="open"
                ref="panelEl"
                class="notification-bell__panel"
                role="dialog"
                aria-label="Notifications"
                :style="{
                    top: panelPos.top + 'px',
                    left: panelPos.left + 'px',
                    width: PANEL_WIDTH + 'px',
                }"
            >
                <header class="notification-bell__header">
                    <h3 class="notification-bell__title">Notifications</h3>
                    <button
                        v-if="unread > 0"
                        type="button"
                        class="notification-bell__action"
                        @click="onMarkAll"
                    >
                        Mark all read
                    </button>
                </header>
                <div class="notification-bell__list">
                    <j-loader v-if="loading" label="Loading notifications…" size="sm" />
                    <p v-else-if="loadError" class="notification-bell__error">{{ loadError }}</p>
                    <p v-else-if="!items.length" class="notification-bell__status">
                        You're all caught up.
                    </p>
                    <button
                        v-for="item in items"
                        v-else
                        :key="item.id"
                        type="button"
                        class="notification-bell__row"
                        :class="[severityClass(item.severity), { 'notification-bell__row--unread': !item.read }]"
                        @click="onRowClick(item)"
                    >
                        <span class="notification-bell__row-dot" aria-hidden="true"></span>
                        <span class="notification-bell__row-body">
                            <span class="notification-bell__row-title">{{ item.title }}</span>
                            <span v-if="item.body" class="notification-bell__row-sub">{{ item.body }}</span>
                            <span class="notification-bell__row-time">
                                {{ formatter.format(new Date(item.occurredAt)) }}
                            </span>
                        </span>
                    </button>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<style scoped>
.notification-bell {
    position: relative;
}
.notification-bell__button {
    background: transparent;
    border: 0;
    color: inherit;
    cursor: pointer;
    padding: 6px;
    border-radius: 8px;
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}
.notification-bell__button:hover {
    background: rgba(255, 255, 255, 0.1);
}
.notification-bell__icon {
    width: 20px;
    height: 20px;
    fill: currentColor;
}
.notification-bell__badge {
    position: absolute;
    top: -2px;
    right: -2px;
    background: #ef4444;
    color: white;
    border-radius: 10px;
    font-size: 0.625rem;
    font-weight: 600;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
}
</style>

<!-- Unscoped: the panel is teleported to <body>, so scoped selectors
     wouldn't reach it. Class names are namespaced to avoid leakage. -->
<style>
.notification-bell__panel {
    position: fixed;
    max-height: 440px;
    background: white;
    color: #0f172a;
    border-radius: 10px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    z-index: 1000;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.notification-bell__header {
    padding: 12px 14px;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.notification-bell__title {
    margin: 0;
    font-size: 0.875rem;
    font-weight: 600;
}
.notification-bell__action {
    background: transparent;
    border: 0;
    color: var(--brand-primary, #1e3a8a);
    cursor: pointer;
    font: inherit;
    font-size: 0.75rem;
    padding: 4px 6px;
}
.notification-bell__action:hover {
    text-decoration: underline;
}
.notification-bell__list {
    overflow-y: auto;
    flex: 1;
}
.notification-bell__status,
.notification-bell__error {
    margin: 0;
    padding: 16px 14px;
    color: #475569;
    font-size: 0.8125rem;
}
.notification-bell__error {
    color: #b91c1c;
}
.notification-bell__row {
    width: 100%;
    background: transparent;
    border: 0;
    border-bottom: 1px solid #f1f5f9;
    text-align: left;
    cursor: pointer;
    padding: 12px 14px;
    font: inherit;
    color: inherit;
    display: grid;
    grid-template-columns: 10px 1fr;
    gap: 10px;
    align-items: start;
}
.notification-bell__row:last-child {
    border-bottom: 0;
}
.notification-bell__row:hover {
    background: #f8fafc;
}
.notification-bell__row-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-top: 6px;
    background: transparent;
}
.notification-bell__row--unread .notification-bell__row-dot {
    background: var(--brand-primary, #1e3a8a);
}
.notification-bell__row--warn .notification-bell__row-title::before {
    content: '⚠ ';
    color: #d97706;
}
.notification-bell__row--error .notification-bell__row-title::before {
    content: '✕ ';
    color: #dc2626;
}
.notification-bell__row-body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}
.notification-bell__row-title {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #0f172a;
}
.notification-bell__row:not(.notification-bell__row--unread) .notification-bell__row-title {
    font-weight: 500;
    color: #475569;
}
.notification-bell__row-sub {
    font-size: 0.75rem;
    color: #475569;
}
.notification-bell__row-time {
    font-size: 0.6875rem;
    color: #94a3b8;
    margin-top: 2px;
}
</style>
